import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Grid, TextField, Button, IconButton, Typography, Box,
  CircularProgress, Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Close as CloseIcon, Store as StoreIcon, Save as SaveIcon } from '@mui/icons-material';

const STORE_FIELDS = [
  { name: 'storeName',    label: 'Store Name',   required: true, grid: 6 },
  { name: 'storeCode',    label: 'Store Code',   grid: 6 },
  { name: 'email',        label: 'Email',        grid: 6, type: 'email' },
  { name: 'phoneNumber',  label: 'Phone',        grid: 6 },
  { name: 'street',       label: 'Street',       grid: 8 },
  { name: 'streetNumber', label: 'Street No.',   grid: 4 },
  { name: 'postCode',     label: 'Post Code',    grid: 4 },
  { name: 'city',         label: 'City',         grid: 4 },
  { name: 'country',      label: 'Country',      grid: 4 },
  { name: 'ibaNumber',    label: 'IBAN',         grid: 6 },
  { name: 'bicNumber',    label: 'BIC',          grid: 6 },
  { name: 'description',  label: 'Description',  grid: 12, multiline: true, rows: 2 },
];

const EMPTY_STORE = {
  storeName: '', storeCode: '', email: '', phoneNumber: '',
  street: '', streetNumber: '', postCode: '', city: '', country: '',
  ibaNumber: '', bicNumber: '', description: '', isActive: true,
};

// Map both PascalCase and camelCase API fields to camelCase form keys
function normaliseStore(store) {
  if (!store) return EMPTY_STORE;
  const form = { ...EMPTY_STORE };
  STORE_FIELDS.forEach(({ name }) => {
    const pascal = name.charAt(0).toUpperCase() + name.slice(1);
    form[name] = store[name] ?? store[pascal] ?? '';
  });
  form.isActive = store.IsActive ?? store.isActive ?? true;
  return form;
}

const StoreDialog = ({ open, mode = 'create', store = null, onClose, onSave }) => {
  const [form, setForm]     = useState(EMPTY_STORE);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(mode === 'edit' && store ? normaliseStore(store) : { ...EMPTY_STORE });
    setError('');
  }, [open, mode, store]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.storeName?.trim()) { setError('Store name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(form, store?.Id ?? store?.id ?? null);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to save store. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={onClose}
      maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <StoreIcon sx={{ color: 'white', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>
                {mode === 'create' ? 'Add Store' : 'Edit Store'}
              </Typography>
              {mode === 'edit' && store && (
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {store.StoreName || store.storeName}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          {STORE_FIELDS.map(({ name, label, required, grid, type, multiline, rows }) => (
            <Grid item xs={12} sm={grid} key={name}>
              <TextField
                fullWidth size="small" label={label} name={name}
                required={!!required} type={type || 'text'}
                multiline={!!multiline} rows={rows}
                value={form[name] ?? ''}
                onChange={handleChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.isActive} name="isActive"
                  onChange={handleChange} size="small"
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: form.isActive ? '#2e7d32' : 'text.secondary' }}>
                  {form.isActive ? 'Active' : 'Inactive'}
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button
          onClick={onClose} disabled={saving} color="inherit"
          sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave} variant="contained" disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
          sx={{
            fontWeight: 700, textTransform: 'none', borderRadius: 1.5, px: 2.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' },
            '&.Mui-disabled': { background: '#e0e0e0' },
          }}
        >
          {saving ? 'Saving…' : mode === 'create' ? 'Add Store' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreDialog;
